import { useCallback, useEffect, useState } from "react";
import { Globe2, Loader2, RefreshCw, RotateCcw, Sparkles, CheckCircle2, Clock, XCircle, ClipboardCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/* Admin → Geo Content Engine: queue, published counts, failed jobs with retry. */

/* Live progress of the city-directory guide generation batch (13 directories). */
function DirectoryBatchCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/geo/directory-batch-status").then((r) => setS(r.data)).catch(() => setS({}));
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const start = async () => {
    setBusy(true);
    try {
      await api.post("/geo/directory-batch", {});
      toast.success("Directory batch started");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start"); }
    finally { setBusy(false); }
  };

  const b = s?.batch || {};
  const total = b.total || 0;
  const done = b.done || 0;
  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const running = b.status === "running" && s?.task_alive;

  return (
    <div className="bg-white border border-zinc-300 p-5" data-testid="directory-batch-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 className="font-heading font-900 text-lg flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-[#4285F4]" /> City Directory Guide Batch
          {s && (
            <span className={`px-2 py-0.5 text-xs font-800 ${running ? "bg-blue-100 text-blue-700" : b.status === "finished" ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-600"}`}
              data-testid="directory-batch-status-pill">
              {running ? "RUNNING" : (b.status || "idle").toUpperCase()}
            </span>
          )}
        </h2>
        {s && !running && (
          <button onClick={start} disabled={busy} data-testid="directory-batch-start"
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#4285F4] text-white text-sm font-700 hover:opacity-90 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {b.status === "finished" ? "Re-run (catch stragglers)" : "Start / Resume batch"}
          </button>
        )}
      </div>
      {!s ? <div className="text-sm text-muted-foreground">Loading…</div> : !b.status ? (
        <p className="text-sm text-muted-foreground">No batch has been run yet.</p>
      ) : (
        <>
          <div className="h-4 bg-zinc-100 border border-zinc-200 overflow-hidden" title={`${pct}%`}>
            <div className="h-full bg-[#34A853] transition-all duration-700" style={{ width: `${pct}%` }}
              data-testid="directory-batch-bar" />
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm">
            <span className="font-800" data-testid="directory-batch-progress">{done} / {total} done ({pct}%)</span>
            <span className="text-[#34A853] font-700">{b.generated || 0} generated</span>
            {(b.failed || 0) > 0 && <span className="text-[#EA4335] font-700">{b.failed} failed</span>}
            {running && b.current && (
              <span className="text-muted-foreground truncate max-w-xs" data-testid="directory-batch-current">
                now: <span className="font-mono">{b.current}</span>
              </span>
            )}
          </div>
          {b.started_at && (
            <p className="text-xs text-muted-foreground mt-1">
              Started {new Date(b.started_at).toLocaleString()} · updates every 15s · survives restarts (auto-resume)
            </p>
          )}
        </>
      )}
    </div>
  );
}

const STATUS_COLOR = {
  PUBLISHED: "#34A853", QUEUED: "#FBBC05", GENERATING: "#4285F4",
  COLLECTING: "#4285F4", VERIFYING: "#4285F4", FAILED: "#EA4335",
};

export default function AdminGeoContent() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const [slugInput, setSlugInput] = useState("");
  const [audit, setAudit] = useState(null);
  const [auditBusy, setAuditBusy] = useState(false);

  const runAudit = async () => {
    setAuditBusy(true);
    try {
      const r = await api.get("/destinations/admin/content-audit");
      setAudit(r.data);
      toast.success(`Audit done — ${r.data.with_issues} of ${r.data.total} destinations have gaps`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Audit failed"); }
    finally { setAuditBusy(false); }
  };

  const load = useCallback(() => {
    api.get("/admin/geo-content/stats").then((r) => setD(r.data)).catch(() => setD({}));
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const generate = async (payload, key) => {
    setBusy(key);
    try {
      const r = await api.post("/admin/geo-content/generate", payload);
      toast.success(`Queued: ${(r.data.queued || []).join(", ") || "nothing to do"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to queue"); }
    finally { setBusy(null); }
  };

  const startDestBatch = async (force = false) => {
    if (force && !window.confirm("Regenerate ALL destination guides from scratch on the latest format? This replaces every existing guide.")) return;
    setBusy("dest");
    try {
      const r = await api.post("/admin/geo-content/run-destinations", force ? { force: true } : {});
      toast.success(r.data.status === "already_running" ? "Batch already running" : force ? "Force regeneration started — every guide will be rebuilt fresh" : "Background batch started — guides + events for all destinations");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to start"); }
    finally { setBusy(null); }
  };

  const startCountryBatch = async (tier = "english") => {
    const label = tier === "tourism"
      ? "Bulk-generate guides for top tourism countries (Germany, France, Japan, UAE + 16 more: country + top 15 cities each)? Queues behind any running batch."
      : "Bulk-generate guides for English-speaking countries (country + states + top 25 cities each)? Runs in the background for several hours.";
    if (!window.confirm(label)) return;
    setBusy("country");
    try {
      const r = await api.post("/admin/geo-content/run-countries", { tier });
      toast.success(r.data.status === "queued" ? "Queued — will start when the current batch finishes" : `Started: ${(r.data.countries || []).length} countries`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to start"); }
    finally { setBusy(null); }
  };

  const sendDigest = async () => {
    setBusy("digest");
    try {
      const r = await api.post("/admin/geo-content/send-digest");
      toast.success(r.data.sent ? `Digest emailed to ${r.data.to}` : `Digest not sent: ${r.data.reason || "email unavailable"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send digest"); }
    finally { setBusy(null); }
  };

  if (d === null) return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-zinc-400" /></div>;

  const stat = (label, val, color, Icon) => (
    <div className="bg-white border border-zinc-300 p-4">
      <div className="font-heading font-900 text-3xl flex items-center gap-2" style={{ color }}>
        <Icon className="w-6 h-6" /> {val ?? 0}
      </div>
      <div className="text-xs text-muted-foreground font-mono uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
  const bs = d.by_status || {};
  const active = (bs.QUEUED || 0) + (bs.GENERATING || 0) + (bs.COLLECTING || 0) + (bs.VERIFYING || 0);

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="bg-[#34A853] text-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10">
          <h1 className="font-heading font-900 text-3xl sm:text-4xl flex items-center gap-3">
            <Globe2 className="w-8 h-8 text-[#FBBC05]" /> Geo Content Engine
          </h1>
          <p className="text-green-50 mt-2 text-sm">Destination guide generation — queue, published counts, failures & retries. Nightly cron pre-generates top tourist countries; visitor requests jump the queue.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="geo-admin-stats">
          {stat("Published guides", d.published, "#34A853", CheckCircle2)}
          {stat("In queue / generating", active, "#4285F4", Clock)}
          {stat("Failed", bs.FAILED || 0, "#EA4335", XCircle)}
          {stat("Places without guides", d.missing, "#FBBC05", Globe2)}
        </div>

        <DirectoryBatchCard />

        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => generate({ scope: "missing", limit: 5 }, "batch")} disabled={busy === "batch"}
            data-testid="geo-admin-batch"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#4285F4] text-white text-sm font-700 hover:opacity-90 disabled:opacity-50">
            {busy === "batch" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate next 5 missing
          </button>
          <button onClick={() => startDestBatch(false)} disabled={busy === "dest" || d.dest_run?.status === "running"}
            data-testid="geo-admin-dest-batch"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#34A853] text-white text-sm font-700 hover:opacity-90 disabled:opacity-50">
            {busy === "dest" || d.dest_run?.status === "running" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe2 className="w-4 h-4" />}
            {d.dest_run?.status === "running" ? "Destination batch running…" : "Generate ALL destination guides + events"}
          </button>
          <button onClick={() => startDestBatch(true)} disabled={busy === "dest" || d.dest_run?.status === "running"}
            data-testid="geo-admin-dest-force"
            title="Rebuild every destination guide from scratch on the latest format and remove duplicate slugs"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#EA4335] text-white text-sm font-700 hover:opacity-90 disabled:opacity-50">
            <RotateCcw className="w-4 h-4" /> Force-regenerate all
          </button>
          <button onClick={() => startCountryBatch("english")} disabled={busy === "country" || d.country_run?.status === "running"}
            data-testid="geo-admin-country-batch"
            title="Country + states + top 25 cities for USA, UK, Australia, Canada, India, South Africa, Ireland, New Zealand, Singapore"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FBBC05] text-zinc-900 text-sm font-800 hover:opacity-90 disabled:opacity-50">
            {busy === "country" || d.country_run?.status === "running" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {d.country_run?.status === "running" ? "English countries batch running…" : "Generate English-speaking countries"}
          </button>
          <button onClick={() => startCountryBatch("tourism")} disabled={busy === "country"}
            data-testid="geo-admin-tourism-batch"
            title="Country + top 15 cities for France, Spain, Italy, Germany, Japan, UAE, Turkey, Mexico, Thailand, Greece + 10 more. Queues behind a running batch."
            className="inline-flex items-center gap-1.5 px-4 py-2 border-2 border-[#FBBC05] text-zinc-900 text-sm font-800 hover:bg-amber-50 disabled:opacity-50">
            <Globe2 className="w-4 h-4 text-[#FBBC05]" /> Top tourism countries (Tier 2)
          </button>
          <button onClick={sendDigest} disabled={busy === "digest"} data-testid="geo-admin-send-digest"
            title="Emails you the weekly guide freshness digest right now (also sent automatically every Monday)"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-zinc-900 text-white text-sm font-700 hover:opacity-90 disabled:opacity-50">
            {busy === "digest" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Email digest now
          </button>
          <div className="flex gap-1">
            <input value={slugInput} onChange={(e) => setSlugInput(e.target.value.toLowerCase())}
              placeholder="force a slug, e.g. india/kerala/munnar"
              className="border border-zinc-300 px-3 py-2 text-sm w-72" data-testid="geo-admin-slug" />
            <button onClick={() => slugInput && generate({ slug: slugInput }, "slug")} disabled={!slugInput || busy === "slug"}
              data-testid="geo-admin-force"
              className="px-3 py-2 bg-zinc-900 text-white text-sm font-700 disabled:opacity-40">Generate</button>
          </div>
          <button onClick={load} className="ml-auto p-2 text-zinc-400 hover:text-primary" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>

        {d.country_run && (
          <div className="bg-white border border-zinc-300 p-4" data-testid="geo-admin-country-run">
            <div className="flex items-center justify-between mb-2">
              <div className="font-700 text-sm flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#FBBC05]" /> English-speaking countries batch
                <span className="text-[10px] font-700 px-1.5 py-0.5 text-white"
                  style={{ backgroundColor: d.country_run.status === "running" ? "#4285F4" : "#34A853" }}>
                  {d.country_run.status.toUpperCase()}
                </span>
              </div>
              <span className="text-xs text-muted-foreground font-mono">{d.country_run.done}/{d.country_run.total}</span>
            </div>
            <div className="h-2 bg-zinc-100 mb-2">
              <div className="h-2 bg-[#FBBC05] transition-all" style={{ width: `${d.country_run.total ? Math.round((d.country_run.done / d.country_run.total) * 100) : 0}%` }} />
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {d.country_run.current && <span>Now: <span className="font-700 text-zinc-800 font-mono">{d.country_run.current}</span></span>}
              <span>{d.country_run.generated} generated</span>
              <span>{d.country_run.skipped} already done</span>
              {d.country_run.failed > 0 && <span className="text-rose-600">{d.country_run.failed} failed</span>}
            </div>
          </div>
        )}

        {d.dest_run && (
          <div className="bg-white border border-zinc-300 p-4" data-testid="geo-admin-dest-run">
            <div className="flex items-center justify-between mb-2">
              <div className="font-700 text-sm flex items-center gap-2">
                <Globe2 className="w-4 h-4 text-[#34A853]" /> Destination background batch
                <span className="text-[10px] font-700 px-1.5 py-0.5 text-white"
                  style={{ backgroundColor: d.dest_run.status === "running" ? "#4285F4" : "#34A853" }}>
                  {d.dest_run.status.toUpperCase()}
                </span>
              </div>
              <span className="text-xs text-muted-foreground font-mono">{d.dest_run.done}/{d.dest_run.total}</span>
            </div>
            <div className="h-2 bg-zinc-100 mb-2">
              <div className="h-2 bg-[#34A853] transition-all" style={{ width: `${d.dest_run.total ? Math.round((d.dest_run.done / d.dest_run.total) * 100) : 0}%` }} />
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {d.dest_run.current && <span>Now: <span className="font-700 text-zinc-800">{d.dest_run.current}</span></span>}
              <span>{d.dest_run.generated} guides generated</span>
              <span>{d.dest_run.events_crawled} event crawls</span>
              <span>{d.dest_run.skipped} already fresh</span>
              {(d.dest_run.errors || []).length > 0 && <span className="text-rose-600">{d.dest_run.errors.length} errors</span>}
            </div>
            {(d.dest_run.errors || []).length > 0 && (
              <div className="mt-2 text-[10px] font-mono text-rose-600 space-y-0.5">
                {d.dest_run.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="bg-white border border-zinc-300 p-4" data-testid="geo-admin-queue">
            <div className="font-700 text-sm mb-2">Queue ({(d.queue || []).length})</div>
            {(d.queue || []).length === 0 ? <div className="text-xs text-muted-foreground">Queue is empty.</div> :
              d.queue.map((q) => (
                <div key={q.slug} className="flex items-center gap-2 text-xs py-1.5 border-t border-zinc-100 first:border-0">
                  <span className="font-mono truncate flex-1">{q.slug}</span>
                  <span className="text-[10px] font-700 px-1.5 py-0.5 text-white" style={{ backgroundColor: STATUS_COLOR[q.status] || "#71717a" }}>{q.status}</span>
                  <span className="text-muted-foreground">{q.requested_count || 0} req</span>
                </div>
              ))}
          </div>

          <div className="bg-white border border-zinc-300 p-4" data-testid="geo-admin-failed">
            <div className="font-700 text-sm mb-2">Failed jobs ({(d.failed_jobs || []).length})</div>
            {(d.failed_jobs || []).length === 0 ? <div className="text-xs text-muted-foreground">No failures.</div> :
              d.failed_jobs.map((f) => (
                <div key={f.slug} className="py-2 border-t border-zinc-100 first:border-0">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-mono truncate flex-1">{f.slug}</span>
                    <span className="text-muted-foreground">{f.fail_count}× failed</span>
                    <button onClick={() => generate({ slug: f.slug }, f.slug)} disabled={busy === f.slug}
                      data-testid={`geo-admin-retry-${f.slug.replace(/\//g, "-")}`}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-[#EA4335] text-white text-[11px] font-700 hover:opacity-90 disabled:opacity-50">
                      {busy === f.slug ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Retry
                    </button>
                  </div>
                  {f.last_error && <div className="text-[10px] text-rose-600 font-mono mt-1 truncate">{f.last_error}</div>}
                </div>
              ))}
          </div>
        </div>

        <div className="bg-white border border-zinc-300 p-4" data-testid="geo-admin-audit">
          <div className="flex items-center justify-between mb-2">
            <div className="font-700 text-sm flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4 text-[#4285F4]" /> Destination content audit
            </div>
            <button onClick={runAudit} disabled={auditBusy} data-testid="geo-admin-run-audit"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#4285F4] text-white text-xs font-700 hover:opacity-90 disabled:opacity-50">
              {auditBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardCheck className="w-3.5 h-3.5" />}
              {audit ? "Re-run audit" : "Run audit"}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Checks every curated destination for broken city profiles, missing attractions, events, coordinates and AI guides.</p>
          {!audit ? (
            <div className="text-xs text-muted-foreground">No audit run yet.</div>
          ) : (
            <>
              <div className="text-xs font-700 mb-2" data-testid="geo-admin-audit-summary">
                {audit.with_issues === 0
                  ? <span className="text-[#34A853]">All {audit.total} destinations are complete.</span>
                  : <span className="text-[#EA4335]">{audit.with_issues} of {audit.total} destinations have content gaps.</span>}
              </div>
              <div className="max-h-96 overflow-y-auto divide-y divide-zinc-100">
                {audit.items.map((it) => (
                  <div key={it.id} className="flex flex-wrap items-center gap-2 py-1.5 text-xs" data-testid={`audit-row-${it.id}`}>
                    {it.issues.length === 0
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-[#34A853] shrink-0" />
                      : <AlertTriangle className="w-3.5 h-3.5 text-[#EA4335] shrink-0" />}
                    <a href={`/destinations/${it.id}`} target="_blank" rel="noopener noreferrer"
                      className="font-700 hover:underline">{it.name}</a>
                    <span className="text-muted-foreground">{it.country}</span>
                    <span className="text-muted-foreground font-mono">{it.attractions} attr · {it.events} events</span>
                    {it.city_profile === "ok_via_airports" && (
                      <span className="text-[10px] font-700 px-1.5 py-0.5 bg-amber-100 text-amber-700">profile via airport fallback</span>
                    )}
                    <span className="ml-auto flex flex-wrap gap-1">
                      {it.issues.map((iss) => (
                        <span key={iss} className="text-[10px] font-700 px-1.5 py-0.5 bg-rose-100 text-rose-700">{iss}</span>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="bg-white border border-zinc-300 p-4" data-testid="geo-admin-recent">
          <div className="font-700 text-sm mb-2">Recently published</div>
          <div className="flex flex-wrap gap-2">
            {(d.recently_published || []).map((r) => (
              <a key={r.slug} href={`/explore/${r.slug}`} target="_blank" rel="noopener noreferrer"
                className="px-3 py-1.5 border border-[#34A853] text-[#34A853] text-xs font-700 hover:bg-green-50">
                {r.name || r.slug} <span className="text-muted-foreground font-400">{r.published_at?.slice(0, 10)}</span>
              </a>
            ))}
            {(d.recently_published || []).length === 0 && <span className="text-xs text-muted-foreground">Nothing published yet.</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
